/*
  HUSTLESTREET — Search (PHASE 10)
  File: assets/hs-search.js
  Loaded by: sections/hs-search-overlay.liquid, sections/hs-main-search.liquid

  - <hs-search-overlay>: full-width search overlay with Shopify Predictive Search.
    Opens from any link to the search page (without a query) or any [data-hs-search-open] element.
  - form[data-hs-auto-submit]: submits automatically when a <select> changes (sort on search page).
  - Public API: window.HSSearch.open(), window.HSSearch.close()
*/
(function () {
  /* ---------- Auto-submit for sort forms ---------- */
  if (!window.__hsAutoSubmitBound) {
    window.__hsAutoSubmitBound = true;
    document.addEventListener('change', function (event) {
      const field = event.target;
      if (!(field instanceof HTMLSelectElement)) return;
      const form = field.closest('form[data-hs-auto-submit]');
      if (form) form.submit();
    });
  }

  if (!('customElements' in window) || customElements.get('hs-search-overlay')) return;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 250;

  class HsSearchOverlay extends HTMLElement {
    connectedCallback() {
      const firstOverlay = document.querySelector('hs-search-overlay');
      this.inactive = firstOverlay !== this;
      if (this.inactive) return;

      this.sectionId = this.dataset.sectionId;
      this.sectionFile = this.dataset.sectionFile || 'hs-search-overlay';
      this.predictiveUrl = this.dataset.predictiveUrl || '/search/suggest';
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.searchPath = this.normalizePath(
        new URL(this.dataset.searchUrl || '/search', window.location.origin).pathname
      );

      this.panel = this.querySelector('[data-hs-search-panel]');
      this.input = this.querySelector('[data-hs-search-input]');
      this.results = this.querySelector('[data-hs-search-results]');
      this.defaultBlock = this.querySelector('[data-hs-search-default]');
      this.status = this.querySelector('[data-hs-search-status]');
      this.clearButton = this.querySelector('[data-hs-search-clear]');

      this.cache = new Map();
      this.controller = null;
      this.debounceTimer = null;

      this.onDocClick = this.onDocClick.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);

      document.addEventListener('click', this.onDocClick);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      if (this.input) this.input.addEventListener('input', this.onInput);

      window.HSSearch = {
        _el: this,
        open: () => this.open(),
        close: () => this.close()
      };
    }

    disconnectedCallback() {
      if (this.inactive) return;

      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.input) this.input.removeEventListener('input', this.onInput);

      if (this.controller) this.controller.abort();
      window.clearTimeout(this.debounceTimer);
      document.documentElement.classList.remove('hs-search-is-open');

      if (window.HSSearch && window.HSSearch._el === this) {
        delete window.HSSearch;
      }
    }

    /* ---------- Helpers ---------- */

    normalizePath(path) {
      return path.length > 1 ? path.replace(/\/$/, '') : path;
    }

    isSearchLink(link) {
      const href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return false;
      try {
        const url = new URL(href, window.location.origin);
        return (
          url.origin === window.location.origin &&
          this.normalizePath(url.pathname) === this.searchPath &&
          !url.searchParams.get('q')
        );
      } catch (error) {
        return false;
      }
    }

    onSearchPage() {
      return this.normalizePath(window.location.pathname) === this.searchPath;
    }

    /* ---------- Events ---------- */

    onDocClick(event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const opener = event.target.closest('[data-hs-search-open]');
      const link = opener ? null : event.target.closest('a[href]');
      const trigger = opener || (link && this.isSearchLink(link) ? link : null);

      if (!trigger || this.contains(trigger)) return;

      event.preventDefault();

      if (this.onSearchPage()) {
        const pageInput = document.querySelector('[data-hs-search-page-input]');
        if (pageInput) {
          pageInput.scrollIntoView({ block: 'center' });
          pageInput.focus();
          return;
        }
      }

      this.open(trigger);
    }

    onClick(event) {
      if (event.target.closest('[data-hs-search-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest('[data-hs-search-clear]')) {
        event.preventDefault();
        this.input.value = '';
        this.toggleClear();
        this.showDefault();
        this.input.focus();
      }
    }

    onInput() {
      this.toggleClear();
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => {
        this.search(this.input.value);
      }, DEBOUNCE_MS);
    }

    onKeydown(event) {
      if (!this.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );

      if (!items.length) {
        event.preventDefault();
        this.panel.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onSectionSelect(event) {
      if (event.detail && event.detail.sectionId === this.sectionId) this.open();
    }

    onSectionDeselect(event) {
      if (event.detail && event.detail.sectionId === this.sectionId) this.close();
    }

    /* ---------- Open / close ---------- */

    open(trigger) {
      if (this.classList.contains('is-open')) return;

      this.lastFocus = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('hs-search-is-open');

      window.requestAnimationFrame(() => {
        if (this.input) {
          this.input.focus();
          this.input.select();
        } else {
          this.panel.focus();
        }
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;

      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('hs-search-is-open');

      if (this.lastFocus && this.lastFocus.isConnected && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    /* ---------- Predictive search ---------- */

    toggleClear() {
      if (this.clearButton) this.clearButton.hidden = !this.input.value.length;
    }

    async search(rawTerm) {
      const term = (rawTerm || '').trim();

      if (term.length < MIN_CHARS) {
        if (this.controller) this.controller.abort();
        this.showDefault();
        return;
      }

      const cacheKey = term.toLowerCase();
      if (this.cache.has(cacheKey)) {
        this.render(this.cache.get(cacheKey));
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.classList.add('is-loading');

      const url =
        this.predictiveUrl +
        '?q=' + encodeURIComponent(term) +
        '&section_id=' + encodeURIComponent(this.sectionFile) +
        '&resources[type]=product,collection,page,query' +
        '&resources[limit]=' + this.limit +
        '&resources[options][unavailable_products]=last';

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Predictive search failed');

        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const node = doc.querySelector('[data-hs-predictive-results]');
        if (!node) throw new Error('No results markup');

        this.cache.set(cacheKey, node.outerHTML);
        this.render(node.outerHTML);
        this.classList.remove('is-loading');
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderFallback();
        this.classList.remove('is-loading');
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.results.hidden = false;
      if (this.defaultBlock) this.defaultBlock.hidden = true;

      const node = this.results.querySelector('[data-hs-predictive-results]');
      const count = node ? parseInt(node.dataset.resultCount, 10) || 0 : 0;
      this.announce(count > 0 ? count + ' ' + (this.dataset.resultsText || 'results') : this.dataset.noResultsText || 'No results');
    }

    renderFallback() {
      this.results.innerHTML = '';
      const message = document.createElement('p');
      message.className = 'hs-search__empty-text';
      message.textContent = this.dataset.fallbackText || 'Press Enter to see all results.';
      this.results.appendChild(message);
      this.results.hidden = false;
      if (this.defaultBlock) this.defaultBlock.hidden = true;
    }

    showDefault() {
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.defaultBlock) this.defaultBlock.hidden = false;
      this.classList.remove('is-loading');
    }

    announce(text) {
      if (!this.status) return;
      this.status.textContent = '';
      window.setTimeout(() => {
        this.status.textContent = text;
      }, 60);
    }
  }

  customElements.define('hs-search-overlay', HsSearchOverlay);
})();